// LOCAL MOCK ONLY — fabricated financials, koncernstruktur and workplaces to
// preview how leadgen's upcoming enrichment would surface in the CRM.
// Deterministic per company (stable across renders); ~1/3 of companies have NO
// data, to exercise the graceful-empty state. Swap enrichmentFor() for ingested
// data once the ingest contract carries it.

export interface FinancialYear {
  year: number
  revenue: number // SEK
  result: number  // SEK net result (can be negative)
  employees: number
}

export interface GroupCompany {
  orgNo: string
  name: string
  revenue: number          // latest revenue, SEK — for the group rollup
  inPipeline?: boolean      // also a lead in our CRM
  stage?: string            // its CRM stage, when inPipeline
}

export interface CompanyGroup {
  ultimateOwner?: GroupCompany
  parent?: GroupCompany
  subsidiaries: GroupCompany[]
}

export interface Office {
  city: string
  employees: number
  isHq: boolean
}

export interface CompanyEnrichment {
  financials: FinancialYear[] // newest first
  group: CompanyGroup | null
  offices: Office[]
}

export interface CompanyHealth {
  growth: 'growing' | 'stable' | 'declining'
  cagr: number
  profitable: boolean
  risk: boolean
}

function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

const PARENT_SUFFIX = ['Holding AB', 'Group AB', 'Invest AB', 'Förvaltning AB']
const SUB_NAME = ['Nord', 'Syd', 'Tech', 'Service', 'Logistik', 'Konsult', 'Fastigheter', 'Production']
const OFFICE_CITY = ['Stockholm', 'Göteborg', 'Malmö', 'Uppsala', 'Linköping', 'Örebro', 'Helsingborg', 'Jönköping']
const STAGE = ['New', 'Calling', 'Contacted', 'Booked']

/** Derive a glanceable health read from the revenue/result trend. */
export function companyHealth(fin: FinancialYear[]): CompanyHealth {
  const latest = fin[0]
  const oldest = fin[fin.length - 1]
  const years = fin.length - 1
  const cagr = oldest.revenue > 0 && years > 0 ? Math.pow(latest.revenue / oldest.revenue, 1 / years) - 1 : 0
  const growth = cagr > 0.03 ? 'growing' : cagr < -0.03 ? 'declining' : 'stable'
  const profitable = latest.result >= 0
  return { growth, cagr, profitable, risk: growth === 'declining' || !profitable }
}

// Single entry point the UI calls. When leadgen starts sending the real fields,
// return them here (e.g. `if (lead.financials) return {...}`). The mock fallback
// runs ONLY on the local dev server (import.meta.env.DEV) — never in a deployed
// build — so dev/prod never show fabricated numbers, just real-or-empty.
export function resolveEnrichment(
  lead: { id: string; employees: string; company: string },
): CompanyEnrichment | null {
  if (import.meta.env.DEV) return enrichmentFor(lead.id, lead.employees, lead.company)
  return null
}

export function enrichmentFor(
  id: string,
  employeesStr: string,
  company: string,
): CompanyEnrichment | null {
  const h = hash(id)
  if (h % 3 === 0) return null // ~1/3 of companies have no enrichment

  const employees = Math.max(3, Number(employeesStr) || 5 + (h % 200))
  const latestRevenue = employees * (700_000 + (h % 600) * 1_000) // ~0.7–1.3M SEK / employee
  const trend = 0.88 + ((h >> 3) % 30) / 100 // 0.88–1.17 YoY
  const margin = -0.05 + ((h >> 7) % 20) / 100 // −5%..+14%

  const financials: FinancialYear[] = []
  let rev = latestRevenue
  let emp = employees
  for (let y = 2024; y >= 2021; y--) {
    financials.push({ year: y, revenue: Math.round(rev), result: Math.round(rev * margin), employees: Math.round(emp) })
    rev /= trend
    emp = Math.max(2, emp / (1 + (trend - 1) / 2))
  }

  const stem = (company.replace(/\s*AB$/i, '').trim() || 'Nordic').split(' ')[0]
  const org = (seed: number) => `55${6000 + (seed % 3999)}-${1000 + (seed % 8999)}`
  const memberRevenue = (seed: number) => (4 + (seed % 140)) * 1_000_000
  const pipelineFor = (seed: number) => {
    const inP = seed % 5 === 0
    return inP ? { inPipeline: true, stage: STAGE[seed % STAGE.length] } : {}
  }

  let group: CompanyGroup | null = null
  if ((h >> 11) % 100 < 60) { // ~60% of enriched companies are in a group
    const hasParent = (h >> 13) % 2 === 0
    const nSubs = (h >> 15) % 5 // 0–4
    group = {
      ultimateOwner: hasParent && (h >> 19) % 2 === 0
        ? { orgNo: org(h + 7), name: `${stem} ${PARENT_SUFFIX[(h + 2) % PARENT_SUFFIX.length]}`, revenue: memberRevenue(h + 7) }
        : undefined,
      parent: hasParent
        ? { orgNo: org(h + 1), name: `${stem} ${PARENT_SUFFIX[h % PARENT_SUFFIX.length]}`, revenue: memberRevenue(h + 1), ...pipelineFor(h + 1) }
        : undefined,
      subsidiaries: Array.from({ length: nSubs }, (_, i) => ({
        orgNo: org(h + 30 + i),
        name: `${stem} ${SUB_NAME[(h + i) % SUB_NAME.length]} AB`,
        revenue: memberRevenue(h + 30 + i),
        ...pipelineFor(h + 13 + i),
      })),
    }
    if (!group.parent && group.subsidiaries.length === 0) group = null // standalone
  }

  // Workplaces (arbetsställen): HQ + a few sites, headcount split.
  const nOffices = 1 + ((h >> 21) % 4) // 1–4
  const offices: Office[] = []
  let remaining = employees
  for (let i = 0; i < nOffices; i++) {
    const isHq = i === 0
    const share = isHq ? 0.45 + ((h >> i) % 20) / 100 : (i === nOffices - 1 ? 1 : 0.3 + ((h >> (i + 4)) % 25) / 100)
    const e = i === nOffices - 1 ? Math.max(1, remaining) : Math.max(1, Math.round(employees * share))
    remaining -= e
    offices.push({ city: OFFICE_CITY[(h + i * 3) % OFFICE_CITY.length], employees: e, isHq })
  }

  return { financials, group, offices }
}
